import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import type { IntrospectionType } from "../../types/introspection";
import { SchemaColumn, type ColumnDescriptor } from "./SchemaColumn";
import {
  parseSchemaTypes,
  buildTypeMap,
  getRootTypeNames,
  unwrapTypeRef,
} from "./schemaUtils";

interface SchemaColumnBrowserProps {
  introspectionJson: string;
  filter: string;
}

export function SchemaColumnBrowser({ introspectionJson, filter }: SchemaColumnBrowserProps) {
  const [columns, setColumns] = useState<ColumnDescriptor[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const allTypes = useMemo(() => parseSchemaTypes(introspectionJson), [introspectionJson]);
  const typeMap = useMemo(() => buildTypeMap(allTypes), [allTypes]);
  const rootTypeNames = useMemo(() => getRootTypeNames(introspectionJson), [introspectionJson]);

  const filteredTypes = useMemo(() => {
    if (!filter) return allTypes;
    const lower = filter.toLowerCase();
    return allTypes.filter((t) => t.name.toLowerCase().includes(lower));
  }, [allTypes, filter]);

  // Initialize with type list column
  useEffect(() => {
    setColumns([{
      kind: "type-list",
      types: filteredTypes,
      rootTypeNames,
    }]);
  }, [filteredTypes, rootTypeNames]);

  // Auto-scroll right when new column is added
  useEffect(() => {
    if (scrollContainerRef.current && columns.length > 1) {
      requestAnimationFrame(() => {
        scrollContainerRef.current?.scrollTo({
          left: scrollContainerRef.current.scrollWidth,
          behavior: "smooth",
        });
      });
    }
  }, [columns.length]);

  const handleSelectType = useCallback((name: string) => {
    const type = typeMap.get(name);
    if (!type) return;

    setColumns((prev) => {
      // If it's from the type list, mark selection and add detail as column 2
      if (prev[0]?.kind === "type-list") {
        const typeListCol: ColumnDescriptor = {
          ...prev[0],
          selectedName: name,
        };
        const detailCol: ColumnDescriptor = {
          kind: "type-detail",
          type,
          isRoot: rootTypeNames.has(name),
        };
        return [typeListCol, detailCol];
      }

      // Find the column that triggered this navigation
      const clickedColumnIdx = findColumnForType(prev, name);

      // Replace everything after the triggering column
      const newColumns = prev.slice(0, clickedColumnIdx + 1);
      // Clear selection on the last column if it's a type-detail or field-detail
      if (newColumns.length > 0) {
        const lastIdx = newColumns.length - 1;
        const lastCol = newColumns[lastIdx]!;
        if (lastCol.kind === "type-detail") {
          const { selectedFieldName: _, ...rest } = lastCol;
          newColumns[lastIdx] = rest;
        } else if (lastCol.kind === "field-detail") {
          const { selectedArgType: _, selectedReturnType: _r, ...rest } = lastCol;
          newColumns[lastIdx] = { ...rest, selectedReturnType: true };
        }
      }
      newColumns.push({
        kind: "type-detail",
        type,
        isRoot: rootTypeNames.has(name),
      });
      return newColumns;
    });
  }, [typeMap, rootTypeNames]);

  const handleSelectField = useCallback((_typeName: string, fieldName: string) => {
    setColumns((prev) => {
      // Find the column that contains this field
      const colIdx = prev.findIndex(
        (col) => col.kind === "type-detail" && col.type.name === _typeName,
      );
      if (colIdx === -1) return prev;

      const col = prev[colIdx] as Extract<ColumnDescriptor, { kind: "type-detail" }>;
      const fields = col.type.fields ?? col.type.inputFields ?? [];
      const field = fields.find((f) => f.name === fieldName);
      if (!field) return prev;

      // Update selection on clicked column
      const updatedCol: ColumnDescriptor = { ...col, selectedFieldName: fieldName };
      const newColumns = [...prev.slice(0, colIdx), updatedCol];

      const hasArgs = field.args && field.args.length > 0;

      if (hasArgs) {
        // Insert a field-detail column showing args + return type
        newColumns.push({
          kind: "field-detail",
          field,
          parentTypeName: _typeName,
        });
      } else {
        // No args — go directly to the return type
        const unwrapped = unwrapTypeRef(field.type);
        const targetType = unwrapped.name ? typeMap.get(unwrapped.name) : null;
        if (targetType && (targetType.fields || targetType.inputFields || targetType.enumValues || targetType.possibleTypes)) {
          newColumns.push({
            kind: "type-detail",
            type: targetType,
            isRoot: rootTypeNames.has(targetType.name),
          });
        }
      }

      return newColumns;
    });
  }, [typeMap, rootTypeNames]);

  // Handle clicking an argument's type in a field-detail column
  const handleSelectArg = useCallback((parentTypeName: string, fieldName: string, argName: string) => {
    setColumns((prev) => {
      const colIdx = prev.findIndex(
        (col) => col.kind === "field-detail" && col.parentTypeName === parentTypeName && col.field.name === fieldName,
      );
      if (colIdx === -1) return prev;

      const col = prev[colIdx] as Extract<ColumnDescriptor, { kind: "field-detail" }>;
      const arg = col.field.args?.find((a) => a.name === argName);
      if (!arg) return prev;

      const unwrapped = unwrapTypeRef(arg.type);
      const targetType = unwrapped.name ? typeMap.get(unwrapped.name) : null;

      const updatedCol: ColumnDescriptor = { ...col, selectedArgType: argName };
      // Remove selectedReturnType when selecting an arg
      const { selectedReturnType: _, ...cleanCol } = updatedCol as Extract<ColumnDescriptor, { kind: "field-detail" }>;
      const newColumns = [...prev.slice(0, colIdx), cleanCol];

      if (targetType && (targetType.fields || targetType.inputFields || targetType.enumValues || targetType.possibleTypes)) {
        newColumns.push({
          kind: "type-detail",
          type: targetType,
          isRoot: rootTypeNames.has(targetType.name),
        });
      }

      return newColumns;
    });
  }, [typeMap, rootTypeNames]);

  // Handle clicking the return type in a field-detail column
  const handleSelectReturnType = useCallback((parentTypeName: string, fieldName: string) => {
    setColumns((prev) => {
      const colIdx = prev.findIndex(
        (col) => col.kind === "field-detail" && col.parentTypeName === parentTypeName && col.field.name === fieldName,
      );
      if (colIdx === -1) return prev;

      const col = prev[colIdx] as Extract<ColumnDescriptor, { kind: "field-detail" }>;
      const unwrapped = unwrapTypeRef(col.field.type);
      const targetType = unwrapped.name ? typeMap.get(unwrapped.name) : null;

      // Remove selectedArgType, set selectedReturnType
      const { selectedArgType: _, ...cleanCol } = col;
      const updatedCol: ColumnDescriptor = { ...cleanCol, selectedReturnType: true };
      const newColumns = [...prev.slice(0, colIdx), updatedCol];

      if (targetType && (targetType.fields || targetType.inputFields || targetType.enumValues || targetType.possibleTypes)) {
        newColumns.push({
          kind: "type-detail",
          type: targetType,
          isRoot: rootTypeNames.has(targetType.name),
        });
      }

      return newColumns;
    });
  }, [typeMap, rootTypeNames]);

  if (allTypes.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-zinc-600">
        No schema data available
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="flex flex-1 overflow-x-auto overflow-y-hidden"
    >
      {columns.map((col, i) => (
        <SchemaColumn
          key={`${i}-${colKey(col)}`}
          column={col}
          onSelectType={handleSelectType}
          onSelectField={handleSelectField}
          onSelectArg={handleSelectArg}
          onSelectReturnType={handleSelectReturnType}
          isLast={i === columns.length - 1}
        />
      ))}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function colKey(col: ColumnDescriptor): string {
  if (col.kind === "type-list") return "list";
  if (col.kind === "type-detail") return `type-${col.type.name}`;
  return `field-${col.parentTypeName}.${col.field.name}`;
}

/** Find which column a type-selection originated from */
function findColumnForType(columns: ColumnDescriptor[], typeName: string): number {
  // Search from the end — last column that references this type
  for (let i = columns.length - 1; i >= 0; i--) {
    const col = columns[i]!;
    if (col.kind === "type-detail") {
      const fields = col.type.fields ?? col.type.inputFields ?? [];
      if (fields.some((f) => {
        const unwrapped = unwrapTypeRef(f.type);
        return unwrapped.name === typeName;
      })) return i;
      // Also check interfaces and possibleTypes
      if (col.type.interfaces?.some((ref) => ref.name === typeName)) return i;
      if (col.type.possibleTypes?.some((ref) => ref.name === typeName)) return i;
    }
    if (col.kind === "field-detail") {
      // Return type or arg types
      const unwrapped = unwrapTypeRef(col.field.type);
      if (unwrapped.name === typeName) return i;
      if (col.field.args?.some((a) => {
        const uw = unwrapTypeRef(a.type);
        return uw.name === typeName;
      })) return i;
    }
  }
  return columns.length - 1;
}
